import type { Address } from "viem"
import { baseSepolia, sepolia } from "viem/chains"
// Setup
export const DETERMINISTIC_DEPLOYER: Address =
  "0x4e59b44847b379578588920ca78fbf26c0b4956c"
export const CREATE2_FACTORY_DEPLOYER_ADDRESS: Address =
  "0x3fAB184622Dc19b6109349B94811493BF2a45362"
export const ENTRYPOINT_V07_ADDRESS: Address =
  "0x0000000071727De22E5E9d8BAf0edAc6f37da032"
export const ENTRY_POINT_SIMULATIONS_ADDRESS: Address =
  "0x74Cb5e4eE81b86e70f9045036a1C5477de69eE87"

// MEE 2.0.0
export const NEXUS_BOOTSTRAP_ADDRESS_1_2_1_MEE200: Address =
  "0x00000000D3254452a909E4eeD47455Af7E27C289"
export const MEE_VALIDATOR_ADDRESS_1_0_1_MEE200: Address =
  "0x00000000d12897DDAdC2044614A9677B191A2d95"
export const NEXUS_ACCOUNT_FACTORY_ADDRESS_MEE200: Address =
  "0x000000001D1D5004a02bAfAb9de2D6CE5b7B13de"
export const NEXUS_IMPLEMENTATION_ADDRESS_1_2_0_MEE200: Address =
  "0x000000004F43C49e93C970E84001853a70923B03"

// MEE 2.1.0
export const NEXUS_BOOTSTRAP_ADDRESS_1_2_1_MEE210: Address =
  "0x0000003eDf18913c01cBc482C978bBD3D6E8ffA3"
export const MEE_VALIDATOR_ADDRESS_1_0_3_MEE210: Address =
  "0x0000000031ef4155C978d48a8A7d4EDba03b04fE"
export const NEXUS_ACCOUNT_FACTORY_ADDRESS_MEE210: Address =
  "0x0000006648ED9B2B842552BE63Af870bC74af837"
export const NEXUS_IMPLEMENTATION_ADDRESS_1_2_0_MEE210: Address =
  "0x00000000383e8cBe298514674Ea60Ee1d1de50ac"

export const COMPOSABLE_MODULE_ADDRESS: Address =
  "0x00000004430bB055dB66eBef6Fe5Ee1DA9668B10"

// Smart Sessions
export const SMART_SESSION_ADDRESS: Address =
  "0x00000000008bdaba73cd9815d79069c247eb4bda"
export const SUDO_POLICY_ADDRESS: Address =
  "0x0000000000FEEc8D74e3143fBaBbca515358d869"
export const SPENDING_LIMITS_POLICY_ADDRESS: Address =
  "0x000000000033212E272655D8a22402Db819477A6"
export const UNIVERSAL_ACTION_POLICY_ADDRESS: Address =
  "0x0000000000714Cf48FcF88A0bFBa70d313415032"
export const TIME_FRAME_POLICY_ADDRESS: Address =
  "0x0000000000D30f611fA3bf652ac6879428586930"
export const VALUE_LIMIT_POLICY_ADDRESS: Address =
  "0x000000000021dC45451291BCDfc9f0B46d6f0278"

// Ownables
export const OWNABLE_VALIDATOR_ADDRESS: Address =
  "0x000000000013fdB5234E4E3162a810F54d9f7E98"
export const OWNABLE_EXECUTOR_ADDRESS: Address =
  "0x4Fd8d57b94966982B62e9588C27B4171B55E8354"

// Test contracts
export const MINTABLE_ADDRESS: Address =
  "0x1758f42Af7026fBbB559Dc60EcE0De3ef81f665e"
export const COUNTER_ADDRESS: Address =
  "0x14e4829E655F0b3a1793838dDd47273D5341d416"
export const TOKEN_WITH_PERMIT: Address =
  "0x51fdb803fD49f0f5bd03de0400a8F17dA2Aa6999"
export const MOCK_SIGNATURE_VALIDATOR: Address =
  "0x0d0C730F50a6da2725d4CD4eb91Bc678Bd377F7D"
export const MOCK_CALLEE: Address = "0x29FdD9D9A9f8CD8dCa0F4764bf0F959183DF4139"
export const MOCK_TOKEN: Address = "0x0006be192b4E06770eaa624AE7648DBF9051221c"

export const ALL_ADDRESSES: Record<
  string,
  { address: Address; chainId: number }
> = {
  DETERMINISTIC_DEPLOYER: {
    address: DETERMINISTIC_DEPLOYER,
    chainId: sepolia.id
  },
  ENTRYPOINT_V07_ADDRESS: {
    address: ENTRYPOINT_V07_ADDRESS,
    chainId: sepolia.id
  },
  NEXUS_IMPLEMENTATION_ADDRESS_1_2_0_MEE200: {
    address: NEXUS_IMPLEMENTATION_ADDRESS_1_2_0_MEE200,
    chainId: sepolia.id
  },
  NEXUS_ACCOUNT_FACTORY_ADDRESS_MEE200: {
    address: NEXUS_ACCOUNT_FACTORY_ADDRESS_MEE200,
    chainId: sepolia.id
  },
  CREATE2_FACTORY_DEPLOYER_ADDRESS: {
    address: CREATE2_FACTORY_DEPLOYER_ADDRESS,
    chainId: sepolia.id
  },
  ENTRY_POINT_SIMULATIONS_ADDRESS: {
    address: ENTRY_POINT_SIMULATIONS_ADDRESS,
    chainId: sepolia.id
  },
  MEE_VALIDATOR_ADDRESS_1_0_1_MEE200: {
    address: MEE_VALIDATOR_ADDRESS_1_0_1_MEE200,
    chainId: sepolia.id
  },
  SMART_SESSION_ADDRESS: {
    address: SMART_SESSION_ADDRESS,
    chainId: sepolia.id
  },
  SUDO_POLICY_ADDRESS: { address: SUDO_POLICY_ADDRESS, chainId: sepolia.id },
  SPENDING_LIMITS_POLICY_ADDRESS: {
    address: SPENDING_LIMITS_POLICY_ADDRESS,
    chainId: sepolia.id
  },
  UNIVERSAL_ACTION_POLICY_ADDRESS: {
    address: UNIVERSAL_ACTION_POLICY_ADDRESS,
    chainId: sepolia.id
  },
  TIME_FRAME_POLICY_ADDRESS: {
    address: TIME_FRAME_POLICY_ADDRESS,
    chainId: sepolia.id
  },
  OWNABLE_VALIDATOR_ADDRESS: {
    address: OWNABLE_VALIDATOR_ADDRESS,
    chainId: sepolia.id
  },
  OWNABLE_EXECUTOR_ADDRESS: {
    address: OWNABLE_EXECUTOR_ADDRESS,
    chainId: sepolia.id
  },
  VALUE_LIMIT_POLICY_ADDRESS: {
    address: VALUE_LIMIT_POLICY_ADDRESS,
    chainId: sepolia.id
  },
  MINTABLE_ADDRESS: { address: MINTABLE_ADDRESS, chainId: sepolia.id },
  COMPOSABLE_MODULE_ADDRESS: {
    address: COMPOSABLE_MODULE_ADDRESS,
    chainId: sepolia.id
  },
  // Base sepolia
  NEXUS_BOOTSTRAP_ADDRESS_1_2_1_MEE200: {
    address: NEXUS_BOOTSTRAP_ADDRESS_1_2_1_MEE200,
    chainId: baseSepolia.id
  },
  //MEE 2.1.0
  NEXUS_BOOTSTRAP_ADDRESS_1_2_1_MEE210: {
    address: NEXUS_BOOTSTRAP_ADDRESS_1_2_1_MEE210,
    chainId: baseSepolia.id
  },
  MEE_VALIDATOR_ADDRESS_1_0_3_MEE210: {
    address: MEE_VALIDATOR_ADDRESS_1_0_3_MEE210,
    chainId: baseSepolia.id
  },
  NEXUS_ACCOUNT_FACTORY_ADDRESS_MEE210: {
    address: NEXUS_ACCOUNT_FACTORY_ADDRESS_MEE210,
    chainId: baseSepolia.id
  },
  NEXUS_IMPLEMENTATION_ADDRESS_1_2_0_MEE210: {
    address: NEXUS_IMPLEMENTATION_ADDRESS_1_2_0_MEE210,
    chainId: baseSepolia.id
  },
  COUNTER_ADDRESS: { address: COUNTER_ADDRESS, chainId: baseSepolia.id },
  TOKEN_WITH_PERMIT: { address: TOKEN_WITH_PERMIT, chainId: baseSepolia.id },
  MOCK_SIGNATURE_VALIDATOR: {
    address: MOCK_SIGNATURE_VALIDATOR,
    chainId: baseSepolia.id
  },
  MOCK_CALLEE: { address: MOCK_CALLEE, chainId: baseSepolia.id },
  MOCK_TOKEN: { address: MOCK_TOKEN, chainId: baseSepolia.id }
}
